import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "../lib/api";
import axios from "axios";
import { money, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function QuoteViewPage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(undefined); // undefined=loading, null=not found
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/quotes/${token}`).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/public/quotes/${token}/accept`);
      setQuote((q) => ({ ...q, status: "accepted" }));
    } finally { setAccepting(false); }
  };

  if (quote === undefined) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (quote === null) return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">Quote not found or link expired.</div>;

  const accepted = quote.status === "accepted" || quote.status === "converted";

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-primary grid place-items-center"><span className="text-primary-foreground font-heading font-extrabold">K</span></div>
          <span className="font-heading font-bold text-lg">KangarooLedger</span>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h1 className="font-heading text-2xl font-bold">Quote {quote.number}</h1>
              <p className="text-muted-foreground text-sm mt-1">{quote.business_name}{quote.abn ? ` · ABN ${quote.abn}` : ""}</p>
            </div>
            <div className="text-right text-sm text-muted-foreground">
              <div>To: <span className="text-foreground font-medium">{quote.customer_name}</span></div>
              <div className="num">Valid until {fmtDate(quote.valid_until)}</div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-border overflow-hidden">
            <div className="grid grid-cols-12 bg-secondary/50 px-4 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
              <div className="col-span-7">Description</div><div className="col-span-2 text-right">Qty</div><div className="col-span-3 text-right">Amount</div>
            </div>
            {quote.line_items.map((l, i) => (
              <div key={i} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm">
                <div className="col-span-7">{l.description}</div>
                <div className="col-span-2 text-right num">{l.quantity}</div>
                <div className="col-span-3 text-right num">{money(l.line_total)}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col items-end gap-1 text-sm">
            <div className="flex gap-8"><span className="text-muted-foreground">Subtotal</span><span className="num w-28 text-right">{money(quote.subtotal)}</span></div>
            <div className="flex gap-8"><span className="text-muted-foreground">GST</span><span className="num w-28 text-right text-sky-700">{money(quote.gst)}</span></div>
            <div className="flex gap-8 font-bold text-lg"><span>Total</span><span className="num w-28 text-right">{money(quote.total)}</span></div>
          </div>

          {quote.notes && <p className="mt-4 text-sm text-muted-foreground">{quote.notes}</p>}

          <div className="mt-8">
            {accepted ? (
              <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 font-medium" data-testid="quote-accepted">
                <CheckCircle2 className="w-5 h-5" /> Quote accepted — thank you! We'll be in touch shortly.
              </div>
            ) : (
              <Button onClick={accept} disabled={accepting} data-testid="accept-quote-btn" className="w-full h-12 text-base">
                {accepting ? "Accepting…" : "Accept this quote"}
              </Button>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4">Powered by KangarooLedger</p>
      </div>
    </div>
  );
}
